import { useState } from 'react';
import { Card } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  User,
  Briefcase,
  CreditCard,
  Phone,
  UserPlus,
  Wallet,
  Lock,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateAge, SOCIAL_STATUS_LABELS } from '@features/purchase-journey/types';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

// Section Header Component
function SectionHeader({ icon: Icon, title, isReadOnly, isExpanded, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isReadOnly ? 'bg-slate-200' : 'bg-emerald-100'}`}>
          <Icon className={`w-5 h-5 ${isReadOnly ? 'text-slate-600' : 'text-emerald-600'}`} />
        </div>
        <div className="text-right">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          {isReadOnly && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              غير قابل للتعديل
            </span>
          )}
        </div>
      </div>
      {isExpanded ? (
        <ChevronUp className="w-5 h-5 text-slate-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400" />
      )}
    </button>
  );
}

// Read-only Field Display
function ReadOnlyField({ label, value }) {
  return (
    <div className="py-2">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className="font-medium text-slate-900">{value || '-'}</p>
    </div>
  );
}

// Editable Input Field
function EditableField({ label, name, value, onChange, type = 'text', error, placeholder, dir }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">{label}</Label>
      <Input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        dir={dir}
        className={`h-12 ${error ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-emerald-500'}`}
      />
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}

// Currency Input Field
function CurrencyField({ label, name, value, onChange, error }) {
  const handleChange = (e) => {
    const numericValue = e.target.value.replace(/\D/g, '');
    onChange({ target: { name, value: numericValue } });
  };

  const formattedValue = value ? Number(value).toLocaleString('ar-SA') : '';

  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-slate-700">{label}</Label>
      <div className="relative">
        <Input
          type="text"
          inputMode="numeric"
          name={name}
          value={formattedValue}
          onChange={handleChange}
          placeholder="0"
          dir="ltr"
          className={`h-12 pl-16 text-left ${error ? 'border-red-500' : 'border-slate-200 focus:border-emerald-500'}`}
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
          ر.س
        </span>
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
}

export default function UserProfileForm({ profile, onSubmit }) {
  const [expandedSections, setExpandedSections] = useState({
    idInfo: true,
    jobInfo: false,
    loans: false,
    contact: true,
    emergency: true,
    expenses: true,
  });

  const [formData, setFormData] = useState({
    // Contact Information (editable)
    phoneNumber: profile.contactInformation.phoneNumber,
    email: profile.contactInformation.email,
    // Emergency Contact (editable)
    emergencyName: profile.emergencyContact.name,
    emergencyPhone: profile.emergencyContact.phoneNumber,
    emergencyEmail: profile.emergencyContact.email,
    // Monthly Expenses (editable)
    invoices: profile.monthlyExpenses.invoices.toString(),
    rent: profile.monthlyExpenses.rent.toString(),
    workersSalary: profile.monthlyExpenses.workersSalary.toString(),
    transportation: profile.monthlyExpenses.transportation.toString(),
    education: profile.monthlyExpenses.education.toString(),
    health: profile.monthlyExpenses.health.toString(),
    insurance: profile.monthlyExpenses.insurance.toString(),
  });

  const [errors, setErrors] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate phone number (Saudi format)
    if (!formData.phoneNumber || !/^05\d{8}$/.test(formData.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = 'يرجى إدخال رقم جوال صحيح (05xxxxxxxx)';
    }

    // Validate email
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'يرجى إدخال بريد إلكتروني صحيح';
    }

    // Validate emergency contact
    if (!formData.emergencyName || formData.emergencyName.trim().length < 2) {
      newErrors.emergencyName = 'يرجى إدخال اسم جهة الاتصال';
    }
    if (!formData.emergencyPhone || !/^05\d{8}$/.test(formData.emergencyPhone.replace(/\s/g, ''))) {
      newErrors.emergencyPhone = 'يرجى إدخال رقم جوال صحيح';
    }
    if (!formData.emergencyEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emergencyEmail)) {
      newErrors.emergencyEmail = 'يرجى إدخال بريد إلكتروني صحيح';
    }

    // Validate expenses (all required, must be >= 0)
    const expenseFields = ['invoices', 'rent', 'workersSalary', 'transportation', 'education', 'health', 'insurance'];
    expenseFields.forEach((field) => {
      const value = formData[field];
      if (value === '' || value === undefined || isNaN(Number(value)) || Number(value) < 0) {
        newErrors[field] = 'يرجى إدخال قيمة صحيحة';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = document.querySelector('[data-error="true"]');
      firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Build updated profile
    const updatedProfile = {
      ...profile,
      contactInformation: {
        phoneNumber: formData.phoneNumber,
        email: formData.email,
      },
      emergencyContact: {
        name: formData.emergencyName,
        phoneNumber: formData.emergencyPhone,
        email: formData.emergencyEmail,
      },
      monthlyExpenses: {
        invoices: Number(formData.invoices) || 0,
        rent: Number(formData.rent) || 0,
        workersSalary: Number(formData.workersSalary) || 0,
        transportation: Number(formData.transportation) || 0,
        education: Number(formData.education) || 0,
        health: Number(formData.health) || 0,
        insurance: Number(formData.insurance) || 0,
      },
    };

    onSubmit(updatedProfile);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd MMMM yyyy', { locale: ar });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const { idInformation, jobInformation, otherLoans } = profile;
  const age = calculateAge(idInformation.dateOfBirth);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen p-4 bg-gradient-to-br from-slate-50 to-slate-100"
    >
      <div className="max-w-2xl mx-auto py-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">مراجعة البيانات</h1>
          <p className="text-slate-500">يرجى مراجعة بياناتك وتعديل المعلومات القابلة للتعديل</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ID Information - Read Only */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <SectionHeader
              icon={User}
              title="معلومات الهوية"
              isReadOnly
              isExpanded={expandedSections.idInfo}
              onToggle={() => toggleSection('idInfo')}
            />
            <AnimatePresence>
              {expandedSections.idInfo && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 grid grid-cols-2 gap-4 border-t border-slate-100">
                    <ReadOnlyField label="الاسم الكامل" value={idInformation.fullName} />
                    <ReadOnlyField label="تاريخ الميلاد" value={formatDate(idInformation.dateOfBirth)} />
                    <ReadOnlyField label="مكان الميلاد" value={idInformation.placeOfBirth} />
                    <ReadOnlyField label="الحالة الاجتماعية" value={SOCIAL_STATUS_LABELS[idInformation.socialStatus]} />
                    <ReadOnlyField label="الجنسية" value={idInformation.nationality} />
                    <ReadOnlyField label="عدد المعالين" value={idInformation.numberOfDependants.toString()} />
                    <ReadOnlyField label="العمر" value={`${age} سنة`} />
                    <ReadOnlyField label="نوع الهوية" value={idInformation.idType} />
                    <ReadOnlyField label="تاريخ إصدار الهوية" value={formatDate(idInformation.idIssueDate)} />
                    <ReadOnlyField label="تاريخ انتهاء الهوية" value={formatDate(idInformation.idExpiryDate)} />
                    <ReadOnlyField label="مكان إصدار الهوية" value={idInformation.idIssuePlace} />
                    <div className="col-span-2">
                      <ReadOnlyField label="العنوان الوطني" value={idInformation.nationalAddress} />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Job Information - Read Only */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <SectionHeader
              icon={Briefcase}
              title="معلومات العمل"
              isReadOnly
              isExpanded={expandedSections.jobInfo}
              onToggle={() => toggleSection('jobInfo')}
            />
            <AnimatePresence>
              {expandedSections.jobInfo && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 grid grid-cols-2 gap-4 border-t border-slate-100">
                    <ReadOnlyField label="المسمى الوظيفي" value={jobInformation.jobTitle} />
                    <ReadOnlyField label="اسم الشركة" value={jobInformation.companyName} />
                    <ReadOnlyField label="تاريخ الالتحاق" value={formatDate(jobInformation.joiningDate)} />
                    <ReadOnlyField label="تاريخ أول راتب" value={formatDate(jobInformation.firstSalaryDate)} />
                    <ReadOnlyField label="سنوات الخدمة الحالية" value={`${jobInformation.yearsAtCurrentJob} سنة`} />
                    <ReadOnlyField label="إجمالي سنوات الخبرة" value={`${jobInformation.totalExperienceYears} سنة`} />
                    <ReadOnlyField label="يوم صرف الراتب" value={`${jobInformation.salaryDayOfMonth} من كل شهر`} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Other Loans - Read Only */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <SectionHeader
              icon={CreditCard}
              title="القروض الأخرى"
              isReadOnly
              isExpanded={expandedSections.loans}
              onToggle={() => toggleSection('loans')}
            />
            <AnimatePresence>
              {expandedSections.loans && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 grid grid-cols-2 gap-4 border-t border-slate-100">
                    <ReadOnlyField label="قرض شخصي" value={formatCurrency(otherLoans.personalLoan)} />
                    <ReadOnlyField label="قرض عقاري" value={formatCurrency(otherLoans.realEstateLoan)} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Contact Information - Editable */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <SectionHeader
              icon={Phone}
              title="معلومات التواصل"
              isReadOnly={false}
              isExpanded={expandedSections.contact}
              onToggle={() => toggleSection('contact')}
            />
            <AnimatePresence>
              {expandedSections.contact && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-4 border-t border-slate-100">
                    <EditableField
                      label="رقم الجوال"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      type="tel"
                      placeholder="05xxxxxxxx"
                      dir="ltr"
                      error={errors.phoneNumber}
                    />
                    <EditableField
                      label="البريد الإلكتروني"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      type="email"
                      placeholder="example@email.com"
                      dir="ltr"
                      error={errors.email}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Emergency Contact - Editable */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <SectionHeader
              icon={UserPlus}
              title="جهة اتصال للطوارئ"
              isReadOnly={false}
              isExpanded={expandedSections.emergency}
              onToggle={() => toggleSection('emergency')}
            />
            <AnimatePresence>
              {expandedSections.emergency && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-4 border-t border-slate-100">
                    <EditableField
                      label="الاسم"
                      name="emergencyName"
                      value={formData.emergencyName}
                      onChange={handleInputChange}
                      placeholder="اسم جهة الاتصال"
                      error={errors.emergencyName}
                    />
                    <EditableField
                      label="رقم الجوال"
                      name="emergencyPhone"
                      value={formData.emergencyPhone}
                      onChange={handleInputChange}
                      type="tel"
                      placeholder="05xxxxxxxx"
                      dir="ltr"
                      error={errors.emergencyPhone}
                    />
                    <EditableField
                      label="البريد الإلكتروني"
                      name="emergencyEmail"
                      value={formData.emergencyEmail}
                      onChange={handleInputChange}
                      type="email"
                      placeholder="example@email.com"
                      dir="ltr"
                      error={errors.emergencyEmail}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Monthly Expenses - Editable */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <SectionHeader
              icon={Wallet}
              title="المصروفات الشهرية"
              isReadOnly={false}
              isExpanded={expandedSections.expenses}
              onToggle={() => toggleSection('expenses')}
            />
            <AnimatePresence>
              {expandedSections.expenses && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 grid grid-cols-2 gap-4 border-t border-slate-100">
                    <CurrencyField
                      label="الفواتير"
                      name="invoices"
                      value={formData.invoices}
                      onChange={handleInputChange}
                      error={errors.invoices}
                    />
                    <CurrencyField
                      label="الإيجار"
                      name="rent"
                      value={formData.rent}
                      onChange={handleInputChange}
                      error={errors.rent}
                    />
                    <CurrencyField
                      label="رواتب العمالة"
                      name="workersSalary"
                      value={formData.workersSalary}
                      onChange={handleInputChange}
                      error={errors.workersSalary}
                    />
                    <CurrencyField
                      label="المواصلات"
                      name="transportation"
                      value={formData.transportation}
                      onChange={handleInputChange}
                      error={errors.transportation}
                    />
                    <CurrencyField
                      label="التعليم"
                      name="education"
                      value={formData.education}
                      onChange={handleInputChange}
                      error={errors.education}
                    />
                    <CurrencyField
                      label="الصحة"
                      name="health"
                      value={formData.health}
                      onChange={handleInputChange}
                      error={errors.health}
                    />
                    <CurrencyField
                      label="التأمين"
                      name="insurance"
                      value={formData.insurance}
                      onChange={handleInputChange}
                      error={errors.insurance}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Submit Button */}
          <div className="pt-4">
            <Button
              type="submit"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg font-medium"
            >
              متابعة لعروض التمويل
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
